import React  from "react";
import { Server, Settings, Cloud, Check, Zap, Wrench, LifeBuoy, Axe } from "lucide-react";
import { useGetServices } from "../../functions/serviceHook";
import ServiceGridSkeleton from './Skeleton';

interface Service {
  _id: string;
  name: string;
  description: string;
  price: number;
  serviceType: 'installation' | 'customization' | 'support' | 'maintenance' | 'other';
}

interface ServiceSelectorProps {
  selectedServices: string[];
  onSelect: (selectedIds: string[]) => void;
}

const ServiceSelector: React.FC<ServiceSelectorProps> = ({ selectedServices, onSelect }) => {
  const { services, isLoading, isError, error } = useGetServices();

  const getServiceIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'installation':
        return <Wrench className="h-5 w-5 text-navy-900" />;
      case 'customization':
        return <Settings className="h-5 w-5 text-navy-900" />;
      case 'support':
        return <LifeBuoy className="h-5 w-5 text-navy-900" />;
      case 'maintenance':
        return <Axe className="h-5 w-5 text-navy-900" />;
      case 'other':
        return <Server className="h-5 w-5 text-navy-900" />;
      default:
        return <Cloud className="h-5 w-5 text-navy-900" />;
    }
  };

  const handleSelect = (serviceId: string) => {
    const newSelection = selectedServices.includes(serviceId)
      ? selectedServices.filter(id => id !== serviceId)
      : [...selectedServices, serviceId];
    
    onSelect(newSelection);
  };

  if (isLoading) {
    return <ServiceGridSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
        <svg
          className="w-6 h-6 mb-2 text-red-600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 115.636 5.636a9 9 0 0112.728 12.728z"></path>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01"></path>
        </svg>
        <p className="font-semibold">Error al cargar los servicios</p>
        <p className="text-sm">{error?.message || "Ocurrió un problema inesperado."}</p>
      </div>
    );
  }
  

  return (
    <div>
      {/* Header Section */}
      <div className="mb-8 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-navy-900 mb-4">
            Additional Services
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Enhance your license with these optional services (multiple selection allowed)
          </p>
          <div className="h-1 w-24 bg-navy-900 mx-auto rounded-full" />
        </div>
      </div>



      

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.length === 0 ? (
          <p className="text-center text-gray-600">No hay servicios disponibles en este momento.</p>
        ) : (
          services.map((service: Service) => (
            <div
              key={service.id}
              className={`relative p-4 border rounded-xl cursor-pointer transition-all
                ${
                  selectedServices.includes(service._id)
                    ? "border-navy-900 bg-navy-50 shadow-lg"
                    : "border-gray-200 bg-white hover:border-navy-700 hover:shadow-md"
                }`}
              onClick={() => handleSelect(service._id)}
            >
              {selectedServices.includes(service._id) && (
                <div className="absolute top-2 right-2 bg-navy-900 p-1 rounded-full">
                  <Check className="h-4 w-4 text-white" />
                </div>
              )}

              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-navy-100 rounded-lg">
                  {getServiceIcon(service.serviceType)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                  <p className="text-sm text-gray-500">{service.description}</p>
                </div>
              </div>

              <div className="flex justify-between items-center mb-4">
                <span className="text-2xl font-bold text-navy-900">
                  ${service.price}<span className="text-sm text-gray-500 ml-2">/month</span>
                </span>
                <span className="bg-navy-100 text-navy-900 px-3 py-1 rounded-full text-sm">
                  {service.serviceType}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Zap className="h-4 w-4 text-navy-700" />
                <span>Automatic scaling</span>
                <span className="mx-2">•</span>
                <span>99.9% SLA</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ServiceSelector;


